import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { AxiosError } from 'axios';
import { Location } from 'history';
import client from 'lib/CommonApi/client';
import stringUtils from 'common/stringUtils';
import {
  callAlertWeb,
  clickDownloadWeb,
  clickShareWeb,
  openMapWeb,
  phoneCallWeb
} from 'common/appInterface';
import StringUtils from 'common/stringUtils';
import { useLoading } from 'components/Loading/LoadingProvider';

const ApprovalDetailPortal: React.FC<ApprovalDetailProps> = (props) => {
  const portal = document.getElementById("portal");
  return ReactDOM.createPortal(<ApprovalDetailPortalStep1 { ...props } />, portal!);
}

const ApprovalDetailPortalStep1: React.FC<ApprovalDetailProps> = ({ tradeApprovalSeq, tradeDate, onClose }) => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** 다국어처리 */
  const { t } = useTranslation();

  const { showLoading, hideLoading } = useLoading();

  const screenId = "TRANS03";
  const screenName = t("TRAN03.title");

  const [approvalDetail, setApprovalDetail] = useState<ApprovalDetail>();

  /** 승인내역 상세검색 */
  const callApprovalDetail = useCallback(async () => {
    showLoading();
    await client
      .get('/api/app/approval/selectApprovalInfo', {
        params: {
          ...location.state.query,
          tradeDate: tradeDate,
          tradeApprovalSeq: tradeApprovalSeq
        }
      })
      .then((response) => {
        let result = response.data.response;

        setApprovalDetail({
          merchantName: result.merchantName,
          cardProductCodeName: result.cardProductCodeName,
          salesKindName: t(`TRAN02.auth.${result.salesKindDivisionCode}`),
          approvalDate:
            result.approvalDate.substring(0, 4) +
            '.' +
            result.approvalDate.substring(4, 6) +
            '.' +
            result.approvalDate.substring(6, 8) +
            '(' +
            stringUtils.getDayOfTheWeek(result.approvalDate) +
            ') ' +
            result.approvalTime.substring(0, 2) +
            ':' +
            result.approvalTime.substring(2, 4),
          approvalTime: '',
          cardApprovalAmount:
            (result.salesKindDivisionCode === '05' ? '' : '+') +
            stringUtils.moneyFormat(result.cardApprovalAmount),
          salesAmount: stringUtils.moneyFormat(
            (
              Number(result.salesAmount) - Number(result.exceptAmount)
            ).toString()
          ),
          taxAmount: stringUtils.moneyFormat(result.taxAmount),
          serviceFee: stringUtils.moneyFormat(result.serviceFee),
          exceptAmount: stringUtils.moneyFormat(result.exceptAmount),
          merchantTelNo: stringUtils.hpFormat(
            stringUtils.nvlDefault(result.telNo1) +
              stringUtils.nvlDefault(result.telNo2) +
              stringUtils.nvlDefault(result.telNo3)
          ),
          merchantAddress:
            stringUtils.nvlDefault(result.merchantRoadAddress01) +
            ' ' +
            stringUtils.nvlDefault(result.merchantRoadAddress02),
          merchantBizNo: stringUtils.nvlDefault(result.merchantBizNo),
          officerName: stringUtils.nvlDefault(result.officerName),
          businessName: stringUtils.nvlDefault(result.sectors),
          cardApprovalNo: result.cardApprovalNo,
          cardNo: result.detailCardNo
          // slipPurchaseYn : t(`TRAN03.purchase.${result.slipPurchaseYn}`)
        });
      })
      .catch((e: AxiosError) => {
        callAlertWeb('ERROR', '99', e.message);
      })
      .finally(() => {
        hideLoading();
      });
  }, [location, tradeApprovalSeq, tradeDate, t, showLoading, hideLoading]);

  useEffect(() => {
    callApprovalDetail();

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  return (
    <>
      {approvalDetail && <ApprovalDetailPortalChild screenId={screenId} screenName={screenName} detail={approvalDetail} onClose={onClose}></ApprovalDetailPortalChild>}
    </>
  );
}

const ApprovalDetailPortalChild: React.FC<ApprovalDetailStep2Props> = ({ screenId, screenName, detail, onClose }) => {
  /** 다국어처리 */
  const { t } = useTranslation();

  /** 공유하기 요청 (clickShare_Web (WEB > APP)) */
  const clickShare = useCallback(() => {
    console.log(JSON.stringify(detail));
    clickShareWeb(screenId, screenName, JSON.stringify(detail));
  }, [screenId, screenName, detail]);

  /** (2024.02.01) 다운로드 요청 (clickDownload_Web (WEB -> APP) */
  const clickDownload = useCallback(() => {
    clickDownloadWeb(screenId, screenName, JSON.stringify(detail));
  }, [screenId, screenName, detail],);

  /** 전화걸기 요청 (phoneCall_Web (WEB > APP)) */
  const clickPhoneCall = useCallback((telNo: string) => {
    phoneCallWeb(telNo);
  }, []);

  /** 구글지도 호출 (openMap_Web (WEB > APP)) */
  const clickOpenMap = useCallback((address: string) => {
    openMapWeb(address);
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return (() => { document.body.style.overflow = "auto" });
  }, []);

  return (
    <div id="wrap" className="bgColorTy01" style={{ position: "fixed", left: 0, top: 0, zIndex: 100, height: "100vh" }}>
      <div id="container" className="pcHidden mycardhistory">
      <section className="contents pb64-mb">
          <div className="inner">
            <div className="receipt">
              <div className="payNam">
                <dl>
                  <dt>{detail.merchantName}</dt>
                  <dd>{detail.cardProductCodeName}</dd>
                  <dd>{detail.approvalDate}</dd>
                </dl>
              </div>
              <div className="payCon place">
                <dl>
                  <dt>{t("TRAN03.header01")}</dt>
                  <dd><span>{detail.salesAmount}</span> KRW</dd>
                </dl>
                {
                  (!StringUtils.checkValueNull(detail.exceptAmount) && detail.exceptAmount !== '0') &&
                  (<dl>
                    <dt>{t("TRAN03.header19")}</dt>
                    <dd><span>{detail.exceptAmount}</span> KRW</dd>
                  </dl>)
                }
                <dl>
                  <dt>{t("TRAN03.header02")}</dt>
                  <dd><span>{detail.taxAmount}</span> KRW</dd>
                </dl>
                <dl>
                  <dt>{t("TRAN03.header03")}</dt>
                  <dd><span>{detail.serviceFee}</span> KRW</dd>
                </dl>
                <dl className="totWrap">
                  <dt>{t("TRAN03.header12")}</dt>
                  <dd><span>{detail.cardApprovalAmount}</span> KRW</dd>
                </dl>
              </div>
              <div className="payCon">
                <dl>
                  <dt>{t("TRAN03.header04")}</dt>
                  <dd><span style={{ color: "#222", textDecoration: "underline", textUnderlinePosition: "under" }} onClick={() => clickPhoneCall(detail.merchantTelNo)}>{detail.merchantTelNo}</span></dd>
                </dl>
                <dl>
                  <dt>{t("TRAN03.header05")}</dt>
                  <dd style={{ maxWidth: "75%" }}><span style={{ color: "#222", textDecoration: "underline", textUnderlinePosition: "under" }} onClick={() => clickOpenMap(detail.merchantAddress)}>{detail.merchantAddress}</span></dd>
                </dl>
                <dl>
                  <dt>{t("TRAN03.header06")}</dt>
                  <dd><span>{stringUtils.bizNoFormat(detail.merchantBizNo)}</span></dd>
                </dl>
                <dl>
                  <dt>{t("TRAN03.header07")}</dt>
                  <dd><span>{detail.officerName}</span></dd>
                </dl>
                <dl>
                  <dt>{t("TRAN03.header08")}</dt>
                  <dd><span>{detail.businessName}</span></dd>
                </dl>
                <dl>
                  <dt>{t("TRAN03.header09")}</dt>
                  <dd><span>{detail.cardApprovalNo}</span></dd>
                </dl>
                <dl>
                  <dt>{t("TRAN03.header10")}</dt>
                  <dd><span>{detail.cardNo}</span></dd>
                </dl>
                {/*<dl>
                  <dt>{t("TRAN03.header11")}</dt>
                  <dd><span>{detail.slipPurchaseYn}</span></dd>
                </dl>*/}
              </div>
            </div>
          </div>
        </section>
        <div className="btnArea">
          <div className="btnWrapTy01">
            <button type="button" className="btnPrimaryTy03" onClick={clickShare}><span>{t("TRAN03.button01")}</span></button>
            <button type="button" className="btnPrimaryTy01" onClick={clickDownload}><span>{t("TRAN03.button02")}</span></button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApprovalDetailPortal;